import React, { useState } from 'react';
import { Head, Section, SectionHeader } from '@/components';
import { CASE_STUDIES } from '@/data/constants';
import type { CaseStudy } from '@/types';

/**
 * Individual case study item with interactive screenshot gallery.
 */
const CaseStudyItem: React.FC<{ study: CaseStudy; index: number }> = ({ study, index }) => {
  const [selectedImage, setSelectedImage] = useState(study.image);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const handleImageChange = (newImage: string) => {
    if (newImage === selectedImage) return;
    setIsTransitioning(true);
    setTimeout(() => {
      setSelectedImage(newImage);
      setIsTransitioning(false);
    }, 150);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center">
      <div className={`order-1 ${index % 2 === 1 ? 'lg:order-2' : ''}`}>
        <div className="relative rounded-3xl aspect-square md:aspect-4/3 overflow-hidden group shadow-[0_30px_80px_rgba(0,0,0,0.55)] ring-1 ring-white/10 bg-linear-to-br from-white/10 via-obsidian-50/60 to-obsidian-200/60">
          {study.link ? (
            <a href={study.link} target="_blank" rel="noopener noreferrer" className="block w-full h-full cursor-pointer">
              {study.video ? (
                <video 
                  src={study.video}
                  autoPlay
                  loop
                  muted
                  playsInline
                  className="w-full h-full object-contain bg-transparent transition-transform duration-700 group-hover:scale-105 opacity-80 group-hover:opacity-100"
                />
              ) : (
                <img 
                  src={selectedImage} 
                  alt={study.title} 
                  loading={index === 0 ? "eager" : "lazy"}
                  decoding="async"
                  className={`w-full h-full object-contain bg-transparent transition-all duration-300 ${isTransitioning ? 'opacity-0 scale-95' : 'opacity-80 group-hover:opacity-100'}`}
                />
              )}
              <div className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <span className="px-6 py-3 bg-white text-obsidian font-bold rounded-full transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">Visit Site</span>
              </div>
            </a>
          ) : (
            study.video ? (
              <video 
                src={study.video}
                autoPlay
                loop
                muted
                playsInline
                className="w-full h-full object-contain bg-transparent transition-transform duration-700 group-hover:scale-105 opacity-80 group-hover:opacity-100"
              />
            ) : (
              <img 
                src={selectedImage} 
                alt={study.title} 
                loading={index === 0 ? "eager" : "lazy"}
                decoding="async"
                className={`w-full h-full object-contain bg-transparent transition-all duration-300 ${isTransitioning ? 'opacity-0 scale-95' : 'opacity-80 group-hover:opacity-100'}`}
              />
            )
          )}
        </div>
      </div>
      <div className={`order-2 ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
        <span className="text-xs font-bold uppercase tracking-widest text-sapphire-500 mb-2 block">{study.client}</span>
        {study.link ? (
          <a href={study.link} target="_blank" rel="noopener noreferrer" className="hover:text-sapphire-500 transition-colors inline-block">
            <h2 className="text-3xl md:text-4xl font-display mb-6 text-white group-hover:text-sapphire-500 transition-colors">{study.title}</h2>
          </a>
        ) : (
          <h2 className="text-3xl md:text-4xl font-display mb-6 text-white">{study.title}</h2>
        )}
        <div className="space-y-6 text-gray-400 leading-relaxed mb-8">
          <p><strong className="text-white text-xs uppercase tracking-wide block mb-1">Challenge</strong> {study.problem}</p>
          <p><strong className="text-white text-xs uppercase tracking-wide block mb-1">Solution</strong> {study.approach}</p>
          <p><strong className="text-white text-xs uppercase tracking-wide block mb-1">Result</strong> {study.outcome}</p>
        </div>
        
        <div className="border-t border-white/10 pt-6">
          <span className="text-xs font-bold uppercase tracking-widest text-gray-500 block mb-3">Features</span>
          <div className="flex flex-wrap gap-2">
            {study.stack.map(tech => (
              <span key={tech} className="px-3 py-1 bg-white/5 border border-white/10 text-xs font-medium text-gray-300 rounded-full hover:bg-white/10 transition-colors">
                {tech}
              </span>
            ))}
          </div>
        </div>

        {/* Screenshot Gallery */}
        {study.screenshots && study.screenshots.length > 0 && (
          <div className="border-t border-white/10 pt-6 mt-6">
            <span className="text-xs font-bold uppercase tracking-widest text-gray-500 block mb-3">Project Screenshots</span>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {/* Main preview thumbnail */}
              <button
                onClick={() => handleImageChange(study.image)}
                className={`aspect-[16/10] rounded-lg overflow-hidden border transition-all duration-200 ${
                  selectedImage === study.image 
                    ? 'bg-obsidian-50/60 ring-2 ring-sapphire-500 ring-offset-2 ring-offset-obsidian border-sapphire-500' 
                    : 'bg-obsidian-50/60 border-white/10 opacity-70 hover:opacity-100 hover:border-white/30'
                }`}
              >
                <img 
                  src={study.image} 
                  alt={`${study.title} preview`}
                  className="w-full h-full object-contain bg-transparent"
                  loading="lazy"
                  decoding="async"
                />
              </button>
              {study.screenshots.map((screenshot, idx) => (
                <button 
                  key={idx}
                  onClick={() => handleImageChange(screenshot)}
                  className={`aspect-[16/10] rounded-lg overflow-hidden border transition-all duration-200 ${
                    selectedImage === screenshot 
                      ? 'bg-obsidian-50/60 ring-2 ring-sapphire-500 ring-offset-2 ring-offset-obsidian border-sapphire-500' 
                      : 'bg-obsidian-50/60 border-white/10 opacity-70 hover:opacity-100 hover:border-white/30'
                  }`}
                >
                  <img 
                    src={screenshot} 
                    alt={`${study.title} screenshot ${idx + 1}`}
                    className="w-full h-full object-contain bg-transparent"
                    loading="lazy"
                    decoding="async"
                  />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Work/Portfolio page component.
 * Displays detailed case studies with alternating image-text layout.
 */
const Work: React.FC = () => {
  return (
    <>
      <Head 
        title="Our Work | Kivosystems"
        description="Success stories from our clients. Websites, stores, and tools that deliver results."
        canonicalUrl="https://www.kivosystems.com/work"
        url="https://www.kivosystems.com/work"
        image="/logo.svg"
        keywords="web design portfolio, case studies, software projects, website examples, client success stories"
      />

      <Section className="bg-obsidian">
        <SectionHeader 
          label="Portfolio" 
          title="Solving real problems."
        >
          <p className="text-gray-400 text-sm leading-relaxed">
            We measure our success by the results our clients achieve. Here are a few examples of how we've helped businesses improve and grow.
          </p>
        </SectionHeader>

        <div className="space-y-16 md:space-y-32">
          {CASE_STUDIES.map((study, index) => (
            <CaseStudyItem key={study.id} study={study} index={index} />
          ))}
        </div>
      </Section>
    </>
  );
};

export default Work;
